import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import classnames from 'classnames'
import { Alert, type ITabItem, Tabs } from 'shared/ui'
import { type SegmentBox } from 'entities/Segment'
import { type FilterEditorStore } from 'widgets/FilterEditor'
import { EnumEnrollmentMode, type IApplyChangeModal } from 'widgets/ContactEnrollment/store/types'
import { type EnrollmentListStore } from '../store'
import { EnrollmentContactsTable } from './EnrollmentContactsTable'
import { EnrollmentInclude } from './EnrollmentInclude/EnrollmentInclude'
import { EnrollmentExclude } from './EnrollmentExclude/EnrollmentExclude'
import styles from './styles.module.scss'
import { EnrollmentActionsProvider } from './EnrollmentActions'

export type IEnrollmentModalProps = {
  enrollmentListStore: EnrollmentListStore
  filterEditorStore: FilterEditorStore
  segmentBox: SegmentBox
  excludeFilterEditorStore: FilterEditorStore
  excludeSegmentBox: SegmentBox
  onConfirm: () => void
  onCancel: () => void
  onClose: () => void
  mode: EnumEnrollmentMode
  allowEmptyCount?: boolean
  showExcludeTab?: boolean
  applyChangeModal: IApplyChangeModal
  applyExcludeChangeModal: IApplyChangeModal
}

export const EnrollmentModal = observer(
  ({
    mode,
    allowEmptyCount,
    showExcludeTab = true,
    enrollmentListStore,
    filterEditorStore,
    segmentBox,
    excludeFilterEditorStore,
    excludeSegmentBox,
    onConfirm,
    onCancel,
    applyChangeModal,
    applyExcludeChangeModal,
  }: IEnrollmentModalProps) => {
    const includeTab = useMemo<ITabItem<'include'>>(
      () => ({
        key: 'include',
        name: 'Include',
      }),
      []
    )
    const excludeTab = useMemo<ITabItem<'exclude'>>(
      () => ({
        key: 'exclude',
        name: 'Exclude',
      }),
      []
    )
    const contactTab = useMemo<ITabItem<'contacts'>>(
      () => ({
        key: 'contacts',
        name: 'Contacts',
        count: enrollmentListStore.allTotalCount,
        disabled: enrollmentListStore.loading,
        isFullCount: true,
      }),
      [
        enrollmentListStore.allTotalCount,
        enrollmentListStore.existHubspotFilter,
        enrollmentListStore.hubspotFiltersCount,
        enrollmentListStore.loading,
      ]
    )
    const [tabActive, setTabActive] = useState<'include' | 'exclude' | 'contacts'>('include')
    const selectTitle = enrollmentListStore.selectTitle
    const isEmptyFilters = !enrollmentListStore.filters.allSize
    const isEmptyCount = allowEmptyCount ? false : !enrollmentListStore.allTotalCount
    const selectDisabled =
      enrollmentListStore.hasHubspotFilterSelected && isEmptyCount && !enrollmentListStore.loading
        ? false
        : isEmptyCount || isEmptyFilters || enrollmentListStore.loading
    const showDisabledTooltip = enrollmentListStore.emptyResult

    return (
      <EnrollmentActionsProvider
        onCancel={onCancel}
        onConfirm={onConfirm}
        selectTitle={selectTitle}
        selectDisabled={selectDisabled}
        loading={enrollmentListStore.loading}
        showDisabledTooltip={showDisabledTooltip}
      >
        {mode === EnumEnrollmentMode.ViewOnly ? (
          <div className={styles.rootContainer}>
            <EnrollmentInclude
              filterEditorStore={filterEditorStore}
              enrollmentListStore={enrollmentListStore}
              segmentBox={segmentBox}
              mode={mode}
              showList
              applyChangeModal={applyChangeModal}
            />
          </div>
        ) : (
          <div className={classnames(styles.rootContainer)}>
            <Tabs
              tabs={
                showExcludeTab ? [includeTab, excludeTab, contactTab] : [includeTab, contactTab]
              }
              activeTabKey={tabActive}
              marginInlineTab={24}
              variant={'divider'}
              tabsClassName={showExcludeTab ? styles.enrollmentTabs : undefined}
              handleSelectTab={(value) => {
                applyChangeModal(() => applyExcludeChangeModal(() => setTabActive(value.key)))
              }}
            />
            {enrollmentListStore.hasHubspotFilterSelected && (
              <div className={classnames(styles.bannerContainer)}>
                <Alert
                  item={{
                    type: 'infoLight',
                    desc: 'Contact list will be updated automatically before broadcast is sent',
                  }}
                />
              </div>
            )}
            {tabActive === 'include' && (
              <EnrollmentInclude
                filterEditorStore={filterEditorStore}
                enrollmentListStore={enrollmentListStore}
                segmentBox={segmentBox}
                mode={mode}
                showList
                applyChangeModal={applyChangeModal}
              />
            )}
            {showExcludeTab && tabActive === 'exclude' && (
              <EnrollmentExclude
                excludeFilterEditorStore={excludeFilterEditorStore}
                enrollmentListStore={enrollmentListStore}
                excludeSegmentBox={excludeSegmentBox}
                mode={mode}
                showList
                applyExcludeChangeModal={applyExcludeChangeModal}
              />
            )}
            {tabActive === 'contacts' && (
              <EnrollmentContactsTable enrollmentListStore={enrollmentListStore} />
            )}
          </div>
        )}
      </EnrollmentActionsProvider>
    )
  }
)
